import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getChalets } from '../services/dataService';
import { Chalet, SortOption, Amenity } from '../types';
import { ChaletCard } from '../components/ChaletCard';
import { AMENITIES } from '../services/mockData';
import { Search, MapPin, Users, Minus, Plus, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Overview: React.FC = () => {
  const { t } = useLanguage();
  const routerLocation = useLocation();
  const [chalets, setChalets] = useState<Chalet[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string>('Alle');
  const [amenityFilters, setAmenityFilters] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>('recommended');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Price Filter
  const [priceLimit, setPriceLimit] = useState<number>(1000);
  const [maxPriceAvailable, setMaxPriceAvailable] = useState<number>(1000);

  // Guests Filter
  const [minGuests, setMinGuests] = useState<number>(1);

  // Mobile Filter Toggle
  const [showFilters, setShowFilters] = useState(false);

  // Hydrate filters from Home.tsx navigation state (minimal receiver-only change)
  useEffect(() => {
    const state = (routerLocation.state ?? {}) as { location?: string; guests?: number };
    if (typeof state.location === 'string') setSearchQuery(state.location);
    if (typeof state.guests === 'number' && !Number.isNaN(state.guests)) {
      setMinGuests(Math.max(1, state.guests));
    }
  }, [routerLocation.state]);

  useEffect(() => {
    getChalets().then((data) => {
      setChalets(data);
      // Calculate max price from data for the slider
      const max = Math.max(...data.map(c => c.pricePerNight), 500);
      setMaxPriceAvailable(max);
      setPriceLimit(max);
      setLoading(false);
    });
  }, []);

  const handleAmenityToggle = (id: string) => {
    setAmenityFilters(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const filteredChalets = useMemo(() => {
    let result = [...chalets];

    // Search
    if (searchQuery) {
        result = result.filter(c => 
            c.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
            c.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.province.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }

    // Category
    if (categoryFilter !== 'Alle') {
      result = result.filter(c => c.category === categoryFilter);
    }

    // Guests
    result = result.filter(c => c.maxGuests >= minGuests);

    // Amenities
    if (amenityFilters.length > 0) {
      result = result.filter(c => 
        amenityFilters.every(filter => c.amenities.includes(filter))
      );
    }

    // Price Filter
    result = result.filter(c => c.pricePerNight <= priceLimit);

    // Sort
    if (sortOption === 'price_asc') {
      result.sort((a, b) => a.pricePerNight - b.pricePerNight);
    } else if (sortOption === 'price_desc') {
      result.sort((a, b) => b.pricePerNight - a.pricePerNight);
    }

    return result;
  }, [chalets, categoryFilter, amenityFilters, sortOption, searchQuery, priceLimit, minGuests]);

  const categories = useMemo(() => {
     const cats = new Set<string>();
     // Always include default categories
     ['Bungalow', 'Chalet', 'Natuurhuisje', 'Wellness huisje'].forEach(c => cats.add(c));
     // Add dynamic categories from available chalets
     chalets.forEach(c => cats.add(c.category));
     
     return ['Alle', ...Array.from(cats).sort()];
  }, [chalets]);

  // Generate a key that changes when filters change to trigger re-animation
  const filterKey = useMemo(() => {
    return JSON.stringify({ categoryFilter, amenityFilters, sortOption, searchQuery, priceLimit, minGuests, count: filteredChalets.length });
  }, [categoryFilter, amenityFilters, sortOption, searchQuery, priceLimit, minGuests, filteredChalets.length]);

  return (
    <div className="min-h-screen bg-brand-50 pb-20 animate-in fade-in duration-500">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6">
             <div className="relative mb-4">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                 <input 
                     type="text" 
                     placeholder={t('hero.searchPlaceholder')}
                     className="w-full pl-10 pr-4 py-3 rounded-xl border border-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-900 bg-white"
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                 />
             </div>
             <button 
                onClick={() => setShowFilters(!showFilters)}
                className="w-full bg-white border border-brand-200 text-brand-900 font-bold py-3 px-4 rounded-xl flex items-center justify-between shadow-sm"
             >
                 <span className="flex items-center gap-2"><SlidersHorizontal size={18}/> Filters & Sorteren</span>
                 <ChevronDown size={18} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`}/>
             </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
            
            {/* LEFT SIDEBAR - FILTERS */}
            <aside className={`w-full lg:w-72 flex-shrink-0 space-y-8 bg-white p-6 rounded-3xl border border-brand-100 shadow-sm transition-all duration-300 ${showFilters ? 'block' : 'hidden lg:block'} lg:sticky lg:top-24`}>
                
                {/* Header / Reset */}
                <div className="flex justify-between items-center">
                    <h2 className="font-serif font-bold text-xl text-brand-900">Filters</h2>
                    <button 
                        onClick={() => {setCategoryFilter('Alle'); setAmenityFilters([]); setSearchQuery(''); setPriceLimit(maxPriceAvailable); setMinGuests(1); }}
                        className="text-xs font-bold text-brand-600 hover:text-brand-800 uppercase tracking-wide hover:underline"
                    >
                        {t('filter.reset')}
                    </button>
                </div>

                {/* Desktop Search */}
                <div className="hidden lg:block">
                    <label className="block text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Zoeken</label>
                    <div className="relative group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-brand-600" size={16} />
                        <input 
                            type="text" 
                            placeholder={t('hero.searchPlaceholder')}
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-900 bg-brand-50/30 transition-all text-sm group-hover:bg-brand-50"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* Sort */}
                <div>
                     <label className="block text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Sorteren</label>
                     <div className="relative">
                         <select 
                             value={sortOption} 
                             onChange={(e) => setSortOption(e.target.value as SortOption)}
                             className="w-full px-4 py-2.5 rounded-xl border border-brand-200 bg-white text-stone-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-900 text-sm appearance-none hover:border-brand-300 transition-colors"
                         >
                             <option value="recommended">{t('filter.recommended')}</option>
                             <option value="price_asc">{t('filter.priceAsc')}</option>
                             <option value="price_desc">{t('filter.priceDesc')}</option>
                         </select>
                         <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" size={16} />
                     </div>
                </div>

                <hr className="border-brand-100" />

                {/* Category */}
                <div>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">{t('filter.category')}</h3>
                    <div className="flex flex-col gap-2">
                        {categories.map(cat => {
                            const displayCat = cat === 'Alle' ? t('filter.all') : (t(`cat.${cat}`) === `cat.${cat}` ? cat : t(`cat.${cat}`));
                            const isActive = categoryFilter === cat;
                            return (
                                <button
                                    key={cat}
                                    onClick={() => setCategoryFilter(cat)}
                                    className={`text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                                        isActive 
                                        ? 'bg-brand-900 text-white shadow-md transform scale-105 origin-left' 
                                        : 'text-stone-600 hover:bg-brand-50'
                                    }`}
                                >
                                    {displayCat}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <hr className="border-brand-100" />

                {/* Guests */}
                <div>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">{t('filter.guests')}</h3>
                    <div className="flex items-center justify-between gap-2 bg-brand-50 border border-brand-200 rounded-xl px-2 py-2">
                            <button 
                                onClick={() => setMinGuests(Math.max(1, minGuests - 1))}
                                className="p-2 text-stone-500 hover:text-brand-900 hover:bg-white rounded-lg transition-colors shadow-sm"
                            >
                                <Minus size={16} />
                            </button>
                            <span className="font-bold text-brand-900 flex items-center gap-1.5 min-w-[30px] justify-center">
                                <Users size={14} className="text-stone-400" />
                                {minGuests}
                            </span>
                            <button 
                                onClick={() => setMinGuests(minGuests + 1)}
                                className="p-2 text-stone-500 hover:text-brand-900 hover:bg-white rounded-lg transition-colors shadow-sm"
                            >
                                <Plus size={16} />
                            </button>
                    </div>
                </div>

                {/* Price */}
                <div>
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider">{t('filter.price')}</h3>
                        <span className="text-xs font-bold text-brand-900 bg-brand-100 px-2 py-1 rounded-md">Max €{priceLimit}</span>
                    </div>
                    <input 
                        type="range" 
                        min="0" 
                        max={maxPriceAvailable} 
                        step="10"
                        value={priceLimit} 
                        onChange={(e) => setPriceLimit(Number(e.target.value))}
                        className="w-full h-1.5 bg-brand-200 rounded-lg appearance-none cursor-pointer accent-brand-900"
                    />
                    <div className="flex justify-between text-[10px] text-stone-400 mt-2 font-medium">
                        <span>€0</span>
                        <span>€{maxPriceAvailable}</span>
                    </div>
                </div>

                <hr className="border-brand-100" />

                {/* Facilities */}
                <div>
                     <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-3">{t('filter.facilities')}</h3>
                     <div className="flex flex-wrap gap-2">
                        {AMENITIES.map(amenity => (
                            <button
                                key={amenity.id}
                                onClick={() => handleAmenityToggle(amenity.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 flex items-center gap-2 ${
                                    amenityFilters.includes(amenity.id)
                                    ? 'bg-brand-100 border-brand-300 text-brand-900 shadow-sm'
                                    : 'bg-white border-brand-200 text-stone-500 hover:bg-brand-50'
                                }`}
                            >
                               {t(`amenity.${amenity.id}`)}
                            </button>
                        ))}
                    </div>
                </div>

            </aside>

            {/* RIGHT MAIN CONTENT */}
            <main className="flex-grow w-full min-w-0">
                
                <div className="flex justify-between items-end mb-6">
                    <h2 className="text-2xl font-serif font-bold text-brand-900 animate-fade-in">
                        {filteredChalets.length} {t('home.found')}
                    </h2>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-600"></div>
                    </div>
                ) : (
                    <>
                        {filteredChalets.length === 0 ? (
                            <div className="text-center py-24 bg-white rounded-[2rem] shadow-sm border border-brand-100 animate-fade-in-up">
                                <div className="bg-brand-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <MapPin size={32} className="text-brand-400" />
                                </div>
                                <h3 className="text-xl font-bold text-stone-800 mb-2">{t('home.notFound')}</h3>
                                <p className="text-stone-500 max-w-sm mx-auto">{t('home.notFoundDesc')}</p>
                                <button 
                                    onClick={() => {setCategoryFilter('Alle'); setAmenityFilters([]); setSearchQuery(''); setPriceLimit(maxPriceAvailable); setMinGuests(1); }}
                                    className="mt-8 px-8 py-3 bg-brand-900 text-white rounded-xl font-semibold hover:bg-black transition-colors"
                                >
                                    {t('filter.reset')}
                                </button>
                            </div>
                        ) : (
                            <div key={filterKey} className="grid grid-cols-1 gap-y-8">
                                {filteredChalets.map((chalet, index) => (
                                    <div 
                                        key={chalet.id} 
                                        className="opacity-0 animate-fade-in-up" 
                                        style={{ animationDelay: `${index * 75}ms` }}
                                    >
                                        <ChaletCard chalet={chalet} variant="horizontal" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>

        </div>
      </div>
    </div>
  );
};